/** @module csi/def/View */
define([
	"dojo/_base/array",
	"dojo/_base/declare",
	"dojo/_base/lang",
	"lodash",
	"csi",
	"csi/_base/underscore",
	"csi/config/preferences",
	"csi/constants",
	"csi/card/_base",
	"csi/core/ExtendedPath",
	"csi/def",
	"csi/def/Action",
	"csi/def/Actions",
	"csi/def/Card",
	"csi/def/Heading",
	"csi/def/PreferencePath",
	"csi/def/Toolbar",
	"csi/locale",
	"csi/reflection",
	"csi/util",
	"csi/utility/pathUtility",
	"csi/view",
	"csi/xml"
], function(array, declare, lang, lodash, csi, _, configPreferences, constants, csiCard, ExtendedPath, def, Action, Actions, Card, Heading, PreferencePath,
	Toolbar, locale, reflection, util, {pathCreate}, view, xml) {

	return declare(null, {
		// Boolean: indicates if view supports preferences
		//bPreferences: false,

		// Boolean: true view should use new instance of cache to render view, or if it can reuse global cache
		//bOwnCache: null,

		// Boolean: true if all headings must be read only, false if Relationship path edit status should be ignored
		//bReadOnly: null,

		/** @property {boolean} bRestrictComments - set to true if view not allows edit comment tooltip */
		bRestrictComments: false,

		// Boolean: true if inserted into slim view
		//m_bSlimOverride: false,

		// Boolean: indicates if view key (i.e. used for custom views, etc.) should use resolved class type rather than defined type
		//bUseInstanceKey: false,

		// String: the class name of derived view.Widget to use as controller
		//classController: null,

		// Object: parameters to pass to the controller
		//classControllerParams: null,

		// String: preferred image size: Small, Medium, Large. See csi.image ImageSize enumeration
		defaultImageSize: reflection.IMAGE_SIZE_MEDIUM,

		// Toolbar: view-specific toolbar configuration
		//defToolbar: null,

		/** @property {boolean} includeImageInDefaultCard - set to true to add Image Heading to Dafault Card */
		includeImageInDefaultCard: false,

		// String: class type for which view is configured (could be base class)
		m_idType: null,

		// String: localized name for view
		m_name: null,

		// Class: reflection class owning view
		//m_rcRoot: null,

		// Heading[]: configuration for displaying images in view
		//m_images: null,

		// Heading[]: configuration for displaying attributes in view
		//m_headings: null,

		// FormData: gathers information when view used for action
		// FIX: Move to csi.view.Widget
		//m_formData: null,

		/** @property {string} [pathHintsContext] - identifier for contextual path hints for View Headings (when using root class) */
		//pathHintsContext: null,

		// String: custom-view container key (overrides default key generation)
		//prefKey: null,

		// PreferencePath[]: preferred image size: Small, Medium, Large. See csi.image ImageSize enumeration
		//prefPaths: null,

		/** @property {boolean} supportsImageLabels - set to true if view supports adding image labels as Headings */
		//supportsImageLabels: false,

		// Card[]: cards used to populate view (debug only)
		//usedCards: null,

		// Actions: UI viewing actions that have no effect on data
		//viewActions: null,

		// String[]: array of view types - only more than one if derived from another view(s)
		//_types: null,

		constructor: function(xn) {
			var gd = lang.hitch(util, util.getDef);
			var gp = lang.hitch(util, util.getProperty);

			// required
			this.m_id = gp(xn, "Id");
			this.m_typeView = gp(xn, 'Type') || gp(xn, 'xsi:type');

			// optional
			this.bOwnCache = util.getBooleanProperty(xn, "OwnCache");
			this.bReadOnly = util.getBooleanProperty(xn, "ReadOnly");
			this.bUseInstanceKey = util.getBooleanProperty(xn, "UseInstanceKey");
			this.bUseHelpRootType = util.getBooleanProperty(xn, "UseHelpRootType");
			this.classController = gp(xn, "ControllerClass");
			this.classControllerParams = csi.fromJson(gp(xn, "ControllerClassParams"));
			this.defToolbar = gd(xn, "Toolbar", Toolbar);
			this.prefKey = gp(xn, "CustomViewContainerKey");
			this.prefPaths = util.getDefs(xn, "PreferencePath", PreferencePath);

			this.m_class = gp(xn, "Class", this.m_class);
			this.m_bSlim = util.getBooleanProperty(xn, "Slim", !!this.m_bSlim);
			this.m_defActions = gd(xn, "Actions", Actions);
			this.m_depPaths = util.getProperties(xn, "DependencyPath", "Path");
			this.m_nPageSize = Number(gp(xn, "Page", constants.DEFAULT_QUERY_SIZE));

			// resolve path hints
			this.pathHints = gp(xn, "PathHints");
			if (this.pathHints) {
				this.pathHintsContext = configPreferences.getContextualPathHints(this.pathHints);

				if (!this.pathHintsContext) {
					console.warn("PathHints not found: " + this.pathHints);
				}
			}

			// Stores what size of images should be displayed by default.
			var configuredImageSize = gp(xn, "ImageSize");
			if (configuredImageSize != null) {
				// convert to enum value
				var imageSize = xml.enumCreate(reflection.IMAGE_SIZE_ENUM, configuredImageSize);

				// check that the value is correct
				switch (imageSize) {
					case reflection.IMAGE_SIZE_ICON:
					case reflection.IMAGE_SIZE_SMALLEST:
					case reflection.IMAGE_SIZE_SMALL:
					case reflection.IMAGE_SIZE_MEDIUM:
					case reflection.IMAGE_SIZE_LARGE:
					case reflection.IMAGE_SIZE_LARGEST:
					case reflection.IMAGE_SIZE_ORIGINAL:
						this.defaultImageSize = imageSize;
						break;
					default:
						console.error("View Id=" + this.m_id + ". Invalid value for 'ImageSize' property=" + configuredImageSize);
						break;
				}
			}
		},

		getHtmlKey: function() {
			// summary: return combination of type and id to indicate view
			return def.getHtmlViewKey(this.m_idType, this.m_id);
		},

		toString: function() {
			return (this.m_idType || "") + ":" + this.m_id + ":" + this.m_typeView;
		},

		newHeading: function(xn) {
			return new Heading(xn);
		},

		isReadOnly: function() {
			return this.bReadOnly;
		},

		setReadOnly: function(/*Boolean*/readOnly) {
			this.bReadOnly = readOnly;
		},

		setFormData: function(formData) {
			this.m_formData = formData;
		},

		getFormData: function() {
			return this.m_formData;
		},

		getSource: function() {
			// summary: if view is popup form, then return action controller
			return this.m_formData ? this.m_formData.m_source : null;
		},

		getSourceNode: function() {
			// summary: if view is popup form, then return node in context when popped up
			var source = this.getSource();
			return source && source.getNode();
		},

		getClassController: function() {
			// summary: determine widget class to use for view definition
			var wc = this.classController;
			if (!wc) {
				array.some(this.getTypes(), function(t) {
					return wc = view.getControllerForType(t);
				});
			}

			return wc;
		},

		getLocaleMap: function(/*Class?*/rc, /*Boolean?*/bConfigure) {
			// summary: get localization map for view
			if (rc && !this.m_rcRoot) {
				this.m_rcRoot = rc;
			}

			return locale.getResolver({
				bConfigure: bConfigure,
				typeContext: this.m_rcRoot ? this.m_rcRoot.getType() : this.m_idType,
				typeView: this.m_idType,
				idView: this.m_id
			});
		},

		getKey: function(/*String?*/context) {
			if (this.prefKey) {
				return this.prefKey;
			}

			var type = (this.bUseInstanceKey && this.m_rcRoot) ? this.m_rcRoot.getType() : this.m_idType;
			return def.composeViewKey(this.m_id, type, context);
		},

		getTypeNode: function() {
			return this.m_idType;
		},

		getId: function() {
			return this.m_id;
		},

		getName: function() {
			return this.m_name;
		},

		getType: function() {
			return this.m_typeView;
		},

		getTypes: function() {
			// summary: return array of types of view in inheritance hierarchy (most derived first)
			this._types = this._types || [this.m_typeView];
			return this._types;
		},

		isSlim: function(/*Boolean*/bByDefinition) {
			// summary: determine if view is defined as slim or is contained by slim view
			return this.m_bSlim || (bByDefinition ? false : this.m_bSlimOverride);
		},

		_addViewAction: function(/*Action|String*/a, /*Object?*/params) {
			// summary: add action for view
			if (typeof a === "string") {
				var id = a;
				a = lang.mixin(new Action(), params);
				a.m_id = id;
			} else if (params) {
				lang.mixin(a, params);
			}

			a.resolve();

			this.viewActions = this.viewActions || {};
			this.viewActions[a.m_id] = a;
			return a;
		},

		getAction: function(idAction) {
			// summary: find action with given id and associated with given element
			var action = this.viewActions ? this.viewActions[idAction] : null;

			if (!action && this.m_defActions) {
				action = this.m_defActions.getAction(idAction);
			}

			return action;
		},

		findHeading: function(/*String*/id, /*boolean|String?*/path, /*Heading?*/parentHeading) {
			// summary: find heading based on heading id and path; if path===false, then ignore path when finding heading
			var headings = parentHeading ? [parentHeading.m_subheadings] : _.union(this.getHeadings(), this.getImages());
			var h1;

			if (!path && (path !== false)) {
				path = null;
			}

			def.traverseHeadings(headings, function(h, bSh) {
				// if looking for specific path, do not take sub-headings into account
				if (bSh && (path !== false)) {
					return;
				}

				// find heading that matches given path unless false in which case only id is checked
				if ((h.m_id == id) && ((path === false) || xml.pathsAreSame(h.m_path, path))) {
					h1 = h;
					return true;
				}
			});

			return h1;
		},

		findHeadingBy: function(/*Function*/f) {
			// summary: find heading or image based on some function
			var headings = _.union(this.getHeadings(), this.getImages());
			var h1;

			def.traverseHeadings(headings, function(h, bSh) {
				if (f(h, bSh)) {
					h1 = h;
					return true;
				}
			});

			return h1;
		},

		getImages: function() {
			// summary: return all headings shown in view
			return this.m_images;
		},

		getHeadings: function() {
			// summary: return all headings shown in view
			return this.m_headings;
		},

		getPreferencePaths: function() {
			// summary: get preference paths relative to view's root node
			return this.prefPaths;
		},

		getRelationships: function() {
			return null;
		},

		getPageRelationship: function() {
			return null;
		},

		getPageClass: function(/*Class?*/rc) {
			var r = this.getPageRelationship();
			rc = rc || this.m_rcRoot;
			return r ? r.getTargetClass(rc) : rc;
		},

		getRootClass: function() {
			return this.m_rcRoot;
		},

		getImageLabels: function(/*Class*/rc) {
			// summary: return Enum containing image labels for given class
			return (rc && this.supportsImageLabels) ? rc.imageLabelEnum : null;
		},

		shouldResolveRootClass: function() {
			// summary: indicate if view requires root class for resolving names and attributes
			return !this.getPageRelationship();
		},

		resolve: function(/*Class*/rcRoot, /*Node*/nodeRoot, /*Boolean*/bConfigure) {
			// summary: resolve all view resources with reflection classes for naming and formatting information
			var lr = this.getLocaleMap(rcRoot, bConfigure);
			var type = (rcRoot ? rcRoot.getType() : null) || (nodeRoot ? nodeRoot.$Type : null);

			// resolve view name - do this only after setting root class
			this.m_name = lr.getTypeView(type, this.m_id);

			// if view uses root class whose type is different than where view was defined, then use root class for view key
			if (!this.bUseInstanceKey && (type != this.m_idType) && !this.getPageRelationship()) {
				this.bUseInstanceKey = true;
			}

			// if view is explicitly not read-only, then set all relationships to ignore read-only paths
			if (this.bReadOnly === false) {
				array.forEach(this.getRelationships(), function(r) {
					r.bReadOnly = false;
				});
			}

			// add a key to each relationship that follows the index
			array.forEach(this.getRelationships(true), function(r, i) {
				r.key = i;
			});

			// resolve names for actions
			if (this.defToolbar) {
				this.defToolbar.resolve(lr, rcRoot, nodeRoot);
			}

			if (this.m_defActions) {
				this.m_defActions.resolve(lr, rcRoot, nodeRoot);
			}

			return lr;
		},

		onRemoveHeading: function(/*Heading*/h) {
			// summary: react to removal of heading, i.e. phase is not correct
		},

		onAddHeading: function(/*Heading*/h) {
			// summary: react to adding of heading
		},

		/**
		 * Replace Cards in headings array with Card Headings
		 * @param {Class} rcRoot - root class of view
		 * @param {PathHints} [pathHints] - path hints 
		 */
		populateHeadingsFromCards(rcRoot, pathHints) {
			if (!this.m_headings) {
				return;
			}

			this.usedCards = []
			this._recursivePopulateHeadingsFromCards(rcRoot, pathHints);

			// remove the duplicate headings
			const allHeadings = this.m_headings;
			const getHeadingKey = (heading) => heading.getKey();
			const uniqHeadings = this.m_headings = lodash.uniqBy(allHeadings, getHeadingKey);
			if (allHeadings.length !== uniqHeadings.length) {
				// log warnings for duplicate heading
				const duplicates = lodash.difference(allHeadings, uniqHeadings, getHeadingKey);
				duplicates.forEach((heading) => {
					console.warn(`Duplicate heading in view ${this.getKey()}: ${getHeadingKey(heading)}`);
				});
			}
		},

		/**
		 * Replace Cards in headings array with Card Headings
		 * @param {Class} rcRoot - root class of view
		 * @param {PathHints} [pathHints] - path hints 
		 */
		_recursivePopulateHeadingsFromCards(rcRoot, pathHints) {
			const {m_headings} = this;
			const cardIndices = [];

			m_headings.forEach((h, i) => {
				if (h.m_bCard) {
					cardIndices.push(i);
				}
			});

			if (!cardIndices.length) {
				return;
			}

			let offset = 0;

			cardIndices.forEach((n) => {
				n += offset;

				// allow card reference to override configured card/heading rights
				const {
					m_bHidden,
					m_id,
					m_path,
					m_right,
				} = m_headings[n];

				// find specified card by looking in type definition
				const rc = ExtendedPath.getPathEndClass(rcRoot, m_path, pathHints);
				let card;

				if (rc) {
					def.getClassTypeDef(rc, (t) => {
						card = t.getCard(m_id);

						if (card) {
							card.m_idType = t.m_idType;
						}

						return card;
					});
				}

				// replace card with any headings found
				const headings = this._generateHeadingByCard(rc, m_id, card);
				offset += headings.length - 1;

				this.onRemoveHeading(m_headings[n]);
				m_headings.splice(n, 1);

				headings.forEach((h) => {
					m_headings.splice(n++, 0, h);
					h.m_path = pathCreate(m_path, h.m_path);

					if (m_bHidden) {
						h.m_bHidden = true;
					}

					if (m_right === xml.RIGHT_NONE) {
						h.setRight(m_right);
					}

					if (!h.m_bCard) {
						this.onAddHeading(h);
					}
				});
			});

			// ensure there are no more cards
			this._recursivePopulateHeadingsFromCards(rcRoot, pathHints);
		},

		/**
		 * Generate heading by card or attribute card keys
		 * @param {ReflectionClass | undefined} rc - Reflection class 
		 * @param {string} cardId - id of Card
		 * @param {Card | undefined} card - Card object 
		 */
		_generateHeadingByCard(rc, cardId, card) {
			const {usedCards} = this;
			let headings;

			if (rc && this._useDefaultCard(rc, cardId, card)) {
				headings = csiCard.getAttributeCardKeysForView(rc.type, this.includeImageInDefaultCard).map((key) => {
					const {id, path, prop} = key;
					return _.extend(new Heading(), {
						m_id: id,
						m_path: path,
						m_prop: prop,
					});
				});
			} else if (card) {
				headings = card.parseCardsAndHeadings();
				usedCards.push(card);
			} else {
				headings = [];
				console.warn(`No card ${cardId} in view ${this.getKey()}`);
			}

			return headings;
		},

		/**
		 * Return true if attribute card should be used instead of given card
		 * @param {ReflectionClass | undefined} rc  
		 * @param {string} cardId 
		 * @param {Card | undefined} card 
		 * @return {boolean}
		 */
		_useDefaultCard(rc, cardId, card) {
			const {abstract, type} = rc;
			const defaultCardId = `${type}.Default`;
			const dc = reflection.getDomainClass(type);

			// if this is the default card id, but the attribute card does not exist, then use card definition if it exists
			return cardId === defaultCardId && !abstract && (!!dc?.cardKeys?.length || !card);
		},
	});
});